'use client';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { AskForPriceButton } from '@eliadmin/core/src/components/product/ask-for-price-button';
import { ProductImages } from '@eliadmin/core/src/components/product/detail/product-images';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

export function ProductInfo({ product }: { product: ProductDetailData }) {
  const selectedVariant = useVariantsStore(s => s.selectedVariant);
  const descriptionLength = 200;

  const descriptionToRender =
    selectedVariant?.short_description ||
    selectedVariant?.description ||
    product.short_description ||
    product.description;

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const url = `${pathname}?${searchParams.toString()}#product-tab-description`;

  const [isExpanded, setIsExpanded] = useState(false);

  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product);
  const ASK_FOR_PRICE = priceDisplay.priceOnRequest;

  return (
    <section className={cn('custom-section', 'py-8 sm:py-12 md:py-16 lg:pt-[120px]')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-12 md:flex-row md:items-start md:justify-between',
        )}
      >
        <ProductImages />
        <div className="flex w-full flex-col items-start justify-start gap-12 md:max-w-[486px]">
          <div className="flex w-full flex-col items-start justify-start gap-6">
            <Breadcrumbs breadcrumb={product.breadcrumb} homeText="Domov" product={product} />
            <h1 data-ea-visual-tag="h2" className="font-oswald text-3xl font-medium uppercase text-black sm:text-4xl lg:text-[40px]">
              {product.name}
            </h1>
            {!!descriptionToRender?.length && (
              <div
                className={cn(
                  'flex flex-col items-start justify-start',
                  'ea-product-detail-header__product-description',
                )}
              >
                <HtmlRenderer
                  html={descriptionToRender || ''}
                  className={cn(
                    'ea-product-detail-header__product-description-content text-ellipsis',
                    !isExpanded &&
                      descriptionToRender?.length &&
                      descriptionToRender.length > descriptionLength &&
                      'line-clamp-2',
                  )}
                />
                {!!(
                  descriptionToRender?.length && descriptionToRender.length > descriptionLength
                ) && (
                  <Button
                    asChild
                    variant={'link'}
                    className={cn(
                      'mt-2 h-6 flex-row items-center space-x-1 p-0 underline hover:text-muted-foreground',
                      'ea-product-detail-header__product-description-button',
                    )}
                  >
                    <span
                      className="no-underline hover:text-primary hover:no-underline"
                      onClick={() => {
                        router.replace(url);
                        setIsExpanded(!isExpanded);
                      }}
                      role="button"
                    >
                      {!isExpanded ? (
                        <>
                          Celý popis
                          <FiChevronDown className={cn('h-5 w-5')} />
                        </>
                      ) : (
                        <>
                          Zatvoriť popis
                          <FiChevronUp className={cn('h-5 w-5')} />
                        </>
                      )}
                    </span>
                  </Button>
                )}
              </div>
            )}
            {ASK_FOR_PRICE && (
              <div className={cn('w-full', 'ea-product-detail__price')}>
                <AskForPriceButton product={product} />
              </div>
            )}
          </div>
          <Separator className="bg-[#D4D4D4]" />
          <Paragraph size="lg" className="text-muted-foreground">
            Kód produktu: {product.code}
          </Paragraph>
        </div>
      </div>
    </section>
  );
}
